/**
 * Wall location lines (docs/bim-plan/06 Part 4) — Revit-convention semantics: the stored
 * Path2D IS one specific, named reference line; the wall body derives from it.
 *
 * Sidedness convention (new in 2.6a): a wall path's EXTERIOR side is the LEFT of its
 * drawing direction, and `AssemblyRecord.layers[0]` is the exterior-most layer. The
 * `flipped` instance param mirrors the body to the other side of the stored path —
 * "flip about the location line" — leaving node ids and hostOffsetFt untouched.
 *
 * All geometry consumers (bim-geometry generate3d, plan picking/emphasis, snap targets)
 * derive the CENTERLINE from the stored path via `offsetPolyline(path, centerlineOffsetFt)`
 * and run the pre-2.6 pipeline unchanged in centerline space. Offset 0 (the default
 * wallCenterline, unflipped) short-circuits to the exact pre-2.6 code path.
 */
import type { AssemblyRecord, ParamValue } from './types'
import { assemblyThickness } from './params'

export const LOCATION_LINES = [
  { value: 'wallCenterline', label: 'Wall Centerline' },
  { value: 'coreCenterline', label: 'Core Centerline' },
  { value: 'coreFaceExterior', label: 'Core Face: Exterior' },
  { value: 'coreFaceInterior', label: 'Core Face: Interior' },
  { value: 'finishFaceExterior', label: 'Finish Face: Exterior' },
  { value: 'finishFaceInterior', label: 'Finish Face: Interior' },
] as const

export type LocationLine = (typeof LOCATION_LINES)[number]['value']

// static assertion: types.ts WallReferenceLine (the drafting-witness face token,
// declared there to avoid an import cycle) must stay identical to this union
type MutualExtends<A, B> = A extends B ? (B extends A ? true : never) : never
const _wallRefLineSync: MutualExtends<import('./types').WallReferenceLine, LocationLine> = true
void _wallRefLineSync

export const DEFAULT_LOCATION_LINE: LocationLine = 'wallCenterline'

/**
 * Distance of each reference line from the EXTERIOR face, derived from the layer stack
 * (`layers[]` ordered exterior→interior + `coreBoundary` bracketing layer indices).
 * No assembly → everything collapses to 0 (a zero-thickness wall has one line).
 */
export function referenceOffsets(assembly: AssemblyRecord | null | undefined): Record<LocationLine, number> {
  const T = assemblyThickness(assembly)
  if (!assembly || T <= 0) {
    return {
      wallCenterline: 0, coreCenterline: 0, coreFaceExterior: 0,
      coreFaceInterior: 0, finishFaceExterior: 0, finishFaceInterior: 0,
    }
  }
  const layers = assembly.layers
  const nLayers = layers.length
  const start = Math.max(0, Math.min(assembly.coreBoundary?.start ?? 0, nLayers - 1))
  const end = Math.max(start, Math.min(assembly.coreBoundary?.end ?? nLayers - 1, nLayers - 1))
  let coreStart = 0
  for (let i = 0; i < start; i++) coreStart += layers[i].thicknessFt || 0
  let coreEnd = coreStart
  for (let i = start; i <= end; i++) coreEnd += layers[i].thicknessFt || 0
  return {
    finishFaceExterior: 0,
    coreFaceExterior: coreStart,
    coreCenterline: (coreStart + coreEnd) / 2,
    wallCenterline: T / 2,
    coreFaceInterior: coreEnd,
    finishFaceInterior: T,
  }
}

function isLocationLine(v: ParamValue | undefined): v is LocationLine {
  return typeof v === 'string' && LOCATION_LINES.some((l) => l.value === v)
}

/**
 * FLANGE-MOUNT round (Matt, 2026-09-20): the exterior and interior wall-FACE
 * offsets from the CENTERLINE (feet, along the wall's left normal) — the same
 * ±T/2 a hosted opening's frame already measures wall faces from, just now
 * SIGNED by which face is which. Reads only `flipped` + assembly thickness:
 * per the sidedness convention above (exterior = left, unflipped;
 * `layers[0]` = exterior-most — confirmed against every wall assembly in the
 * firm library, 2026-09-20), the exterior face sits at +T/2 unflipped and
 * flips to -T/2 with the wall's own `flipped` param, independent of whatever
 * `locationLine` the wall happens to store (this is centerline-relative, not
 * stored-path-relative — callers already working in stored-path space, like
 * hostedFootprintCorners, must add their own `centerlineOffsetFt` on top).
 */
export function wallFaceOffsets(
  flipped: ParamValue | undefined,
  assembly: AssemblyRecord | null | undefined,
): { exteriorOff: number; interiorOff: number } {
  const half = assemblyThickness(assembly) / 2
  return flipped ? { exteriorOff: -half, interiorOff: half } : { exteriorOff: half, interiorOff: -half }
}

/**
 * Like `wallFaceOffsets`, but to the STRUCTURAL CORE faces (the stud faces
 * bracketed by `AssemblyRecord.coreBoundary`) instead of the full assembly's
 * finish faces — TEMPORARY DIMENSIONS round (Matt, 2026-09-20): "it should
 * measure to an actual edge of the assembly, biased toward the face of
 * studs," since a face-of-finish reading (or worse, a bare center-to-center
 * approximation) isn't what a framer or inspector reads off a dimension
 * string. Same `referenceOffsets` depth table `wallFaceOffsets` itself could
 * be rebuilt from — reused here rather than re-deriving the layer walk.
 */
export function wallCoreFaceOffsets(
  flipped: ParamValue | undefined,
  assembly: AssemblyRecord | null | undefined,
): { exteriorOff: number; interiorOff: number } {
  const offsets = referenceOffsets(assembly)
  const half = offsets.wallCenterline
  const extOff = half - offsets.coreFaceExterior
  const intOff = half - offsets.coreFaceInterior
  return flipped ? { exteriorOff: -extOff, interiorOff: -intOff } : { exteriorOff: extOff, interiorOff: intOff }
}

/**
 * EVERY layer boundary's signed offset from the wall centerline, exterior to
 * interior (N+1 offsets for N layers — the two outer ones equal
 * `wallFaceOffsets`', not just the two `wallCoreFaceOffsets` picks out).
 * TEMPORARY DIMENSIONS round, cont'd (Matt, 2026-09-20: "I want to be able to
 * move the reference to any layer in a wall, not just from wall to wall") —
 * the stud face is still the sensible DEFAULT a fresh dimension infers, but a
 * drag needs the whole assembly's own reference planes to snap to, exactly
 * like Revit's own temporary-dimension retarget. A zero/absent assembly
 * collapses to the single centerline (no layers to offer).
 */
export function wallLayerFaceOffsets(
  flipped: ParamValue | undefined,
  assembly: AssemblyRecord | null | undefined,
): number[] {
  const T = assemblyThickness(assembly)
  if (!assembly || T <= 0) return [0]
  const half = T / 2
  const depths: number[] = [0]
  let acc = 0
  for (const layer of assembly.layers) {
    acc += layer.thicknessFt || 0
    depths.push(acc)
  }
  const offsets = depths.map((d) => half - d)
  return flipped ? offsets.map((o) => -o) : offsets
}

/**
 * Signed distance from the stored path to the wall CENTERLINE along the path's LEFT
 * normal. Derivation (06 §4.2): with exterior = left (unflipped), a point at depth d
 * from the exterior face sits at P_ext − d·leftNormal, so
 * centerline − reference = −(T/2 − refOffset)·leftNormal; flipping negates the sign.
 * Returns 0 for wallCenterline / no assembly — the delta-0 identity skip.
 */
export function centerlineOffsetFt(
  locationLine: ParamValue | undefined,
  flipped: ParamValue | undefined,
  assembly: AssemblyRecord | null | undefined,
): number {
  // PAINTED WALL FINISHES (wallCladding.ts): place the wall from the TYPE's assembly,
  // then move the centerline by the painted change — the core never moves, the
  // painted finish grows outward
  const painted = assembly as (AssemblyRecord & { paintedFrom?: AssemblyRecord; paintShiftFt?: number }) | null | undefined
  if (painted?.paintedFrom) {
    const d0 = centerlineOffsetFt(locationLine, flipped, painted.paintedFrom)
    const sh = painted.paintShiftFt || 0
    return d0 + (flipped ? -sh : sh)
  }
  const ll: LocationLine = isLocationLine(locationLine) ? locationLine : DEFAULT_LOCATION_LINE
  const offsets = referenceOffsets(assembly)
  const T = assemblyThickness(assembly)
  const raw = T / 2 - offsets[ll]
  if (raw === 0) return 0
  return flipped ? raw : -raw
}
